import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { DB_NAME, DB_VERSION, defaultSettings, defaultVoiceProfile } from "../constants";
import type { AppSettings, Conversation, MediaRecord, MemoryItem, Message, VoiceProfile } from "../types/app";

interface VoiceAppDb extends DBSchema {
  settings: {
    key: string;
    value: AppSettings;
  };
  memories: {
    key: string;
    value: MemoryItem;
    indexes: { "by-category": string; "by-pinned": number };
  };
  conversations: {
    key: string;
    value: Conversation;
  };
  messages: {
    key: string;
    value: Message;
    indexes: { "by-conversation": string };
  };
  media: {
    key: string;
    value: MediaRecord;
    indexes: { "by-type": string };
  };
  voiceProfiles: {
    key: string;
    value: VoiceProfile;
  };
}

let dbPromise: Promise<IDBPDatabase<VoiceAppDb>> | undefined;
let overrideName: string | undefined;

export function setDbNameForTests(name: string): void {
  overrideName = name;
  dbPromise = undefined;
}

export function getDb(): Promise<IDBPDatabase<VoiceAppDb>> {
  if (!dbPromise) {
    dbPromise = openDB<VoiceAppDb>(overrideName ?? DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("settings")) db.createObjectStore("settings", { keyPath: "id" });
        if (!db.objectStoreNames.contains("memories")) {
          const store = db.createObjectStore("memories", { keyPath: "id" });
          store.createIndex("by-category", "category");
          store.createIndex("by-pinned", "isPinned");
        }
        if (!db.objectStoreNames.contains("conversations")) db.createObjectStore("conversations", { keyPath: "id" });
        if (!db.objectStoreNames.contains("messages")) {
          const store = db.createObjectStore("messages", { keyPath: "id" });
          store.createIndex("by-conversation", "conversationId");
        }
        if (!db.objectStoreNames.contains("media")) {
          const store = db.createObjectStore("media", { keyPath: "id" });
          store.createIndex("by-type", "type");
        }
        if (!db.objectStoreNames.contains("voiceProfiles")) db.createObjectStore("voiceProfiles", { keyPath: "id" });
      },
    });
  }
  return dbPromise;
}

export async function ensureSeedData(): Promise<AppSettings> {
  const db = await getDb();
  let settings = await db.get("settings", "default");
  if (!settings) {
    settings = defaultSettings();
    await db.put("settings", settings);
  }
  const voice = await db.get("voiceProfiles", "default-soft");
  if (!voice) await db.put("voiceProfiles", defaultVoiceProfile());
  return settings;
}

export async function putSetting(settings: AppSettings): Promise<void> {
  const db = await getDb();
  await db.put("settings", settings);
}

export async function getSettings(): Promise<AppSettings> {
  return ensureSeedData();
}

export async function listAll<T extends keyof VoiceAppDb>(store: T): Promise<VoiceAppDb[T]["value"][]> {
  const db = await getDb();
  return db.getAll(store as any) as Promise<VoiceAppDb[T]["value"][]>;
}

export async function putRecord<T extends keyof VoiceAppDb>(store: T, value: VoiceAppDb[T]["value"]): Promise<void> {
  const db = await getDb();
  await db.put(store as any, value as never);
}

export async function deleteRecord<T extends keyof VoiceAppDb>(store: T, id: string): Promise<void> {
  const db = await getDb();
  await db.delete(store as any, id);
}

export async function clearStore<T extends keyof VoiceAppDb>(store: T): Promise<void> {
  const db = await getDb();
  await db.clear(store as any);
}

export async function getMessagesByConversation(conversationId: string, limit = 20): Promise<Message[]> {
  const db = await getDb();
  const messages = await db.getAllFromIndex("messages", "by-conversation", conversationId);
  return messages.sort((a, b) => a.createdAt.localeCompare(b.createdAt)).slice(-limit);
}

export async function upsertConversation(conversation: Conversation): Promise<void> {
  await putRecord("conversations", conversation);
}
